import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  type UseUserPreferenceResult,
  useUserPreference,
} from 'shared/lib/preferences';

type Table2ColumnDef = ColumnDef<any, any> & {
  id?: string;
  meta?: {
    disableManagement?: boolean;
    isVisible?: boolean;
  };
};

export type TablePreferences = {
  visibility: VisibilityState;
  order: ColumnOrderState;
  sizing: ColumnSizingState;
};

export type UseTablePreferencesOptions = {
  /**
   * When `true`, table state is persisted to the backend via
   * `useUserPreference`. When `false` (or when `boardId` is missing), the
   * table falls back to in-memory `useState` — state is reset on unmount.
   *
   * Note: we deliberately do NOT fall back to `localStorage`. The legacy
   * Mantine `useLocalStorage` keys (`${id}-column-*`) would orphan after
   * any consumer-side `id` change and produce silent state loss; in-memory
   * state surfaces the missing `boardId` immediately during development.
   */
  persist?: boolean;
  /**
   * Required when `persist` is `true`. Selected board id from the user
   * session.
   */
  boardId?: number;
};

export type UseTablePreferencesResult = {
  state: TablePreferences;
  setVisibility: (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState),
  ) => void;
  setOrder: (
    updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState),
  ) => void;
  setSizing: (
    updater:
      | ColumnSizingState
      | ((prev: ColumnSizingState) => ColumnSizingState),
  ) => void;
  /**
   * Headless preference handle. `null` when `persist === false` (no backend
   * persistence layer to expose, so admin actions are unavailable).
   */
  preference: UseUserPreferenceResult<TablePreferences> | null;
};

const createColumnVisibility = (
  columns: Array<Table2ColumnDef>,
): VisibilityState =>
  columns.reduce((acc, column) => {
    if (column.id) {
      acc[column.id] =
        column.meta?.isVisible === undefined ? true : column.meta.isVisible;
    }
    return acc;
  }, {} as VisibilityState);

const resolveUpdater = <S>(updater: S | ((prev: S) => S), prev: S): S =>
  typeof updater === 'function' ? (updater as (p: S) => S)(prev) : updater;

/**
 * Headless hook reading & writing the three table preference dimensions
 * (column visibility / order / sizing) for a single Table2 instance.
 *
 * When `options.persist` is true and a `boardId` is available, state is
 * served from the backend with three-layer precedence (code → board → user).
 * Otherwise we fall back to the legacy localStorage behaviour to avoid
 * regressions in non-logged contexts.
 */
export const useTablePreferences = (
  id: string,
  columns: Array<Table2ColumnDef>,
  options?: UseTablePreferencesOptions,
): UseTablePreferencesResult => {
  const persist = options?.persist ?? true;
  const boardId = options?.boardId;

  // Dev-only guard: the global axios interceptor in `shared/api` runs
  // `camelizeKeys`/`decamelizeKeys` recursively over every request/response
  // body. Our preference payload is an opaque jsonb blob, so column ids
  // containing `_` or `-` get transformed (e.g. `updated_at` round-trips
  // as `updatedAt`) and TanStack lookups break silently. Force camelCase
  // ids at the source.
  if (import.meta.env.DEV) {
    const nonCamel = columns
      .map(c => c.id)
      .filter((cid): cid is string => Boolean(cid))
      .filter(cid => /[_-]/.test(cid));
    if (nonCamel.length > 0) {
      // biome-ignore lint/suspicious/noConsole: dev-only diagnostic
      console.warn(
        `[useTablePreferences] Column ids must be camelCase to round-trip ` +
          `through the API serializer cleanly. Found non-camelCase ids in ` +
          `table "${id}": ${nonCamel.join(', ')}. ` +
          `These columns will desync on the first PATCH /preferences/{key}.`,
      );
    }
  }

  const codeDefault = useMemo<TablePreferences>(
    () => ({
      visibility: createColumnVisibility(columns),
      order: [],
      sizing: {},
    }),
    [columns],
  );

  // Backend-backed preferences are only enabled when `persist` is true AND
  // we know the boardId. Without boardId the hook is in legacy mode.
  const preference = useUserPreference<TablePreferences>(
    boardId,
    `tables.${id}`,
    codeDefault,
    { enabled: persist && Boolean(boardId) },
  );

  // In-memory fallback when `boardId` is missing or `persist === false`.
  // Lives for the lifetime of the component — no persistence layer.
  const [legacyVisibility, setLegacyVisibility] = useState<VisibilityState>(
    () => codeDefault.visibility,
  );
  const [legacyOrder, setLegacyOrder] = useState<ColumnOrderState>(() => []);
  const [legacySizing, setLegacySizing] = useState<ColumnSizingState>(
    () => ({}),
  );

  const useBackend = persist && Boolean(boardId);

  const state: TablePreferences = useBackend
    ? preference.value
    : {
        visibility: legacyVisibility,
        order: legacyOrder,
        sizing: legacySizing,
      };

  const setVisibility = useCallback(
    (
      updater: VisibilityState | ((prev: VisibilityState) => VisibilityState),
    ) => {
      if (useBackend) {
        preference.setValue(prev => ({
          visibility: resolveUpdater(updater, prev.visibility),
        }));
      } else {
        setLegacyVisibility(prev => resolveUpdater(updater, prev));
      }
    },
    [preference, setLegacyVisibility, useBackend],
  );

  const setOrder = useCallback(
    (
      updater:
        | ColumnOrderState
        | ((prev: ColumnOrderState) => ColumnOrderState),
    ) => {
      if (useBackend) {
        preference.setValue(prev => ({
          order: resolveUpdater(updater, prev.order),
        }));
      } else {
        setLegacyOrder(prev => resolveUpdater(updater, prev));
      }
    },
    [preference, setLegacyOrder, useBackend],
  );

  const setSizing = useCallback(
    (
      updater:
        | ColumnSizingState
        | ((prev: ColumnSizingState) => ColumnSizingState),
    ) => {
      if (useBackend) {
        preference.setValue(prev => ({
          sizing: resolveUpdater(updater, prev.sizing),
        }));
      } else {
        setLegacySizing(prev => resolveUpdater(updater, prev));
      }
    },
    [preference, setLegacySizing, useBackend],
  );

  return {
    state,
    setVisibility,
    setOrder,
    setSizing,
    preference: useBackend ? preference : null,
  };
};
